import axios from 'axios';

import { resolve } from '@/api/endPoints';
import { UPLOAD_TYPE_PREVIEW_IMAGE } from '@/config/constants';
import { getMediaMetadata } from '@/redux/slices/adManager/mediaMetadata';
import axiosClient from '@/utils/axios';

export type OverlayTemplateApiScope =
  | { kind: 'account' }
  | { kind: 'profile'; locationUuid: string }
  | { kind: 'accountPicker'; locationUuid: string };

export type WritableOverlayTemplateApiScope = Exclude<
  OverlayTemplateApiScope,
  { kind: 'accountPicker' }
>;

export const getOverlayTemplateScopeKey = (
  scope: OverlayTemplateApiScope
): string =>
  scope.kind === 'account'
    ? scope.kind
    : `${scope.kind}:${scope.locationUuid}`;

export type OverlayTemplateOrdering = 'pk' | '-pk' | 'title' | '-title';

export interface PaginatedApiResponse<T> {
  count?: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface OverlayTemplateFolderDto {
  uuid: string;
  name: string;
}

export interface OverlayTemplateSummaryDto {
  uuid: string;
  title: string;
  folder_uuid?: string | null;
  thumbnail_url: string;
  aspect_ratio: string;
  created: string;
  modified: string;
}

export interface OverlayTemplatePreviewImageDto {
  uuid: string;
  original_asset?: string;
  preview_image?: string;
  thumbnail_image?: string;
  width?: number;
  height?: number;
  [key: string]: unknown;
}

export interface OverlayTemplateDetailDto extends OverlayTemplateSummaryDto {
  configuration: Record<string, unknown>;
  preview_image: OverlayTemplatePreviewImageDto | null;
  is_account_template: boolean;
  location_uuid: string | null;
}

export interface OverlayTemplateWritePayload {
  title: string;
  folder_uuid?: string | null;
  configuration: Record<string, unknown>;
  preview_image_uuid?: string | null;
  aspect_ratio: string;
}

export interface OverlayTemplateListParams {
  scope: OverlayTemplateApiScope;
  folderUuid?: string | null;
  search?: string;
  ordering?: OverlayTemplateOrdering;
  limit?: number;
  cursor?: string;
}

export interface OverlayTemplatePreviewUploadResult {
  uuid: string;
  uploaded_object_data?: { uuid?: string; [key: string]: unknown };
  [key: string]: unknown;
}

export const getOverlayTemplateScopeQuery = (
  scope: OverlayTemplateApiScope
): Record<string, string> => {
  if (scope.kind === 'profile') {
    return { scope: 'profile', location_uuid: scope.locationUuid };
  }
  if (scope.kind === 'accountPicker') {
    return {
      scope: 'account',
      target_location_uuid: scope.locationUuid
    };
  }
  return { scope: 'account' };
};

const compactQuery = (
  query: Record<string, string | number | null | undefined>
) =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const listOverlayTemplates = async ({
  scope,
  folderUuid,
  search,
  ordering,
  limit,
  cursor
}: OverlayTemplateListParams): Promise<
  PaginatedApiResponse<OverlayTemplateSummaryDto>
> => {
  const url = resolve(
    'overlay_templates',
    {},
    compactQuery({
      ...getOverlayTemplateScopeQuery(scope),
      folder__uuid: folderUuid,
      search,
      ordering,
      limit,
      cursor
    })
  );
  const response = await axiosClient.get<
    PaginatedApiResponse<OverlayTemplateSummaryDto>
  >(url);
  return response.data;
};

export const getOverlayTemplate = async (
  uuid: string,
  scope: OverlayTemplateApiScope
): Promise<OverlayTemplateDetailDto> => {
  const url = resolve(
    'overlay_template',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.get<OverlayTemplateDetailDto>(url);
  return response.data;
};

export const createOverlayTemplate = async (
  payload: OverlayTemplateWritePayload,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateDetailDto> => {
  const url = resolve(
    'overlay_templates',
    {},
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.post<OverlayTemplateDetailDto>(
    url,
    payload
  );
  return response.data;
};

export const updateOverlayTemplate = async (
  uuid: string,
  payload: Partial<OverlayTemplateWritePayload>,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateDetailDto> => {
  const url = resolve(
    'overlay_template',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.patch<OverlayTemplateDetailDto>(
    url,
    payload
  );
  return response.data;
};

export const deleteOverlayTemplate = async (
  uuid: string,
  scope: WritableOverlayTemplateApiScope
): Promise<void> => {
  const url = resolve(
    'overlay_template',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  await axiosClient.delete(url);
};

export const listOverlayTemplateFolders = async (
  scope: OverlayTemplateApiScope
): Promise<PaginatedApiResponse<OverlayTemplateFolderDto>> => {
  let nextUrl: string | null = resolve(
    'overlay_template_folders',
    {},
    { ...getOverlayTemplateScopeQuery(scope), limit: 100 }
  );
  const results: OverlayTemplateFolderDto[] = [];
  const visitedUrls = new Set<string>();

  while (nextUrl) {
    if (visitedUrls.has(nextUrl)) {
      throw new Error('Folder pagination returned a repeated cursor.');
    }
    visitedUrls.add(nextUrl);
    const response = await axiosClient.get<
      PaginatedApiResponse<OverlayTemplateFolderDto>
    >(nextUrl);
    results.push(...response.data.results);
    nextUrl = response.data.next;
  }

  return {
    count: results.length,
    next: null,
    previous: null,
    results
  };
};

export const createOverlayTemplateFolder = async (
  name: string,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateFolderDto> => {
  const url = resolve(
    'overlay_template_folders',
    {},
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.post<OverlayTemplateFolderDto>(url, {
    name
  });
  return response.data;
};

export const updateOverlayTemplateFolder = async (
  uuid: string,
  name: string,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateFolderDto> => {
  const url = resolve(
    'overlay_template_folder',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.patch<OverlayTemplateFolderDto>(url, {
    name
  });
  return response.data;
};

export const deleteOverlayTemplateFolder = async (
  uuid: string,
  scope: WritableOverlayTemplateApiScope
): Promise<void> => {
  const url = resolve(
    'overlay_template_folder',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  await axiosClient.delete(url);
};

export const uploadOverlayTemplatePreview = async ({
  file,
  locationUuid,
  onProgress,
  signal
}: {
  file: File;
  locationUuid?: string;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}): Promise<OverlayTemplatePreviewUploadResult> => {
  const contentType = file.type || 'image/png';
  const mediaMetadata = await getMediaMetadata(file, contentType);
  const uploadUrl = resolve('create_post_media', {}, {});
  const response = await axiosClient.post<OverlayTemplatePreviewUploadResult>(
    uploadUrl,
    compactQuery({
      content_type: contentType,
      filesize_in_bytes: file.size,
      filename: file.name,
      location_uuid: locationUuid,
      upload_type: UPLOAD_TYPE_PREVIEW_IMAGE,
      ...mediaMetadata
    }),
    { signal }
  );
  const uploadPreset = response.data;

  await axios.put(String(uploadPreset.url), file, {
    headers: {
      ...((uploadPreset.headers as Record<string, string>) || {}),
      'Content-Type': contentType
    },
    signal,
    onUploadProgress: (event) => {
      const total = event.total || file.size;
      if (!total) return;
      onProgress?.(
        Math.max(0, Math.min(99, Math.round((event.loaded / total) * 100)))
      );
    }
  });
  await axiosClient.put(
    String(uploadPreset.confirm_url),
    {
      success: true
    },
    { signal }
  );

  return uploadPreset;
};
